import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import CaptureSheet, { type CaptureMode } from './CaptureSheet.js';
import { speechCtor } from './speech.js';

/**
 * The one capture entry point (spec 2026-10-09 §5). Layout mounts this
 * provider; the tab bar's center button, the sidebar button and the c / "/"
 * shortcut call `open()`. Pages that show tasks register `useCaptured` so a
 * capture (or its Undo) reloads them.
 */

interface CaptureApi {
  open(mode?: CaptureMode): void;
  isOpen: boolean;
  /** Tell listeners something was added or undone. */
  notify(): void;
  subscribe(cb: () => void): () => void;
}

const Ctx = createContext<CaptureApi | null>(null);

export function CaptureProvider({ children }: { children: ReactNode }): JSX.Element {
  const [mode, setMode] = useState<CaptureMode | null>(null);
  const listeners = useRef(new Set<() => void>());

  const open = useCallback((m?: CaptureMode) => {
    setMode(m ?? (speechCtor() ? 'voice' : 'type'));
  }, []);
  const notify = useCallback(() => {
    for (const cb of [...listeners.current]) cb();
  }, []);
  const subscribe = useCallback((cb: () => void) => {
    listeners.current.add(cb);
    return () => {
      listeners.current.delete(cb);
    };
  }, []);

  const api = useMemo<CaptureApi>(() => ({ open, isOpen: mode !== null, notify, subscribe }), [open, mode, notify, subscribe]);

  return (
    <Ctx.Provider value={api}>
      {children}
      {mode && <CaptureSheet initialMode={mode} onClose={() => setMode(null)} onChanged={notify} />}
    </Ctx.Provider>
  );
}

const NOOP: Pick<CaptureApi, 'open' | 'isOpen' | 'notify'> = { open: () => undefined, isOpen: false, notify: () => undefined };

/** `open()` the capture sheet. Outside a provider (isolated page tests) it is a no-op. */
export function useCapture(): Pick<CaptureApi, 'open' | 'isOpen' | 'notify'> {
  return useContext(Ctx) ?? NOOP;
}

/** Run `cb` after every capture or Undo (pages reload their lists). */
export function useCaptured(cb: () => void): void {
  const api = useContext(Ctx);
  const ref = useRef(cb);
  ref.current = cb;
  useEffect(() => api?.subscribe(() => ref.current()), [api]);
}
